<!-- Header: stays put while the middle scrolls. On a phone (a bottom sheet) it's also the handle to swipe it closed. -->
<header class="relative flex-shrink-0 overflow-hidden bg-primary text-white px-5 pb-5 md:px-8 md:pb-6"
  [ngClass]="isSheet ? 'pt-2 touch-none select-none' : 'pt-5 md:pt-7'"
  (pointerdown)="dragStart($event)" (pointermove)="dragMove($event)" (pointerup)="dragEnd($event)" (pointercancel)="dragEnd($event)">
  <div *ngIf="isSheet" aria-hidden="true" class="mx-auto mb-3 h-1.5 w-10 rounded-full bg-white/30"></div>

  <div class="relative flex items-start justify-between gap-4">
    <span class="flex items-center justify-center size-12 rounded-xl bg-accent text-primary">
      <mat-icon class="!size-7 !text-[28px]">{{ content.icon }}</mat-icon>
    </span>
    <button type="button" (click)="close()" aria-label="Close"
      class="flex items-center justify-center size-12 -mr-2 -mt-1 rounded-full text-white hover:bg-white/10 active:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent transition-colors duration-200">
      <mat-icon>close</mat-icon>
    </button>
  </div>
  <h2 [id]="titleId" class="relative mt-3 text-2xl md:text-3xl font-bold tracking-tight">{{ content.title }}</h2>
  <p class="relative mt-1 text-white/80">{{ content.tagline }}</p>
  <!-- Live numbers, only once there are enough to say "busy" (MIN_STAT_TO_SHOW) -->
  <p *ngIf="statCount((data.stats$ | async) ?? null) as count" class="relative mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-sm">
    <span aria-hidden="true" class="live-dot size-2 rounded-full bg-accent"></span>
    <span><strong class="font-semibold">{{ count | number }}</strong> {{ content.stat?.label }}</span>
  </p>
</header>

<!-- Body: the one scroller -->
<div #body class="flex-1 min-h-0 overflow-y-auto bg-white">
  <!-- What it looks like: an illustrated example, hanging off the header -->
  <div class="bg-gradient-to-b from-primary from-50% to-white to-50% px-5 pt-3 md:px-8" role="img" [attr.aria-label]="'Example: ' + content.title">
    <div class="relative mx-auto max-w-sm" [ngSwitch]="content.preview" aria-hidden="true">
      <span class="absolute -top-2.5 right-3 z-10 rounded-full bg-white px-2 py-0.5 text-sm font-medium text-primary/70 shadow-sm ring-1 ring-black/5">Example</span>

      <article *ngSwitchCase="'house'" class="flex overflow-hidden rounded-xl bg-white shadow-card-elevated ring-1 ring-black/5">
        <div class="flex w-28 flex-shrink-0 items-center justify-center bg-gradient-to-br from-accent to-accent-dark text-primary/60">
          <mat-icon class="!size-12 !text-5xl">bed</mat-icon>
        </div>
        <div class="min-w-0 p-3">
          <p class="text-lg font-bold leading-tight text-primary">&#8358;350,000<span class="text-xs font-medium text-gray-500">/year</span></p>
          <p class="truncate text-sm font-medium text-gray-900">Self-contain near the LGA office</p>
          <p class="truncate text-xs text-gray-500">Ikeja, Lagos</p>
          <p class="mt-2 flex flex-wrap gap-1">
            <span class="rounded bg-accent/70 px-1.5 py-0.5 text-[11px] font-medium text-primary">Seeking a roommate</span>
            <span class="rounded bg-primary/5 px-1.5 py-0.5 text-[11px] font-medium text-primary">Leaving in Nov</span>
          </p>
        </div>
      </article>

      <article *ngSwitchCase="'sale'" class="flex overflow-hidden rounded-xl bg-white shadow-card-elevated ring-1 ring-black/5">
        <div class="relative flex w-28 flex-shrink-0 items-center justify-center bg-primary/5 text-primary/40">
          <mat-icon class="!size-12 !text-5xl">kitchen</mat-icon>
          <span class="absolute left-2 top-2 rounded bg-white/90 px-1.5 py-0.5 text-[11px] font-medium text-primary shadow-sm">Fairly used</span>
        </div>
        <div class="min-w-0 p-3">
          <p class="flex items-center gap-2 text-lg font-bold leading-tight text-primary">&#8358;45,000
            <span class="rounded bg-accent/70 px-1.5 py-0.5 text-[11px] font-medium leading-none">Negotiable</span>
          </p>
          <p class="truncate text-sm font-medium text-gray-900">Mini fridge</p>
          <p class="truncate text-xs text-gray-500">Yaba, Lagos · 2h ago</p>
          <p class="mt-2 flex flex-wrap gap-1">
            <span class="rounded bg-primary/5 px-1.5 py-0.5 text-[11px] font-medium text-primary">Near you</span>
            <span class="rounded bg-primary/5 px-1.5 py-0.5 text-[11px] font-medium text-primary">Leaving in Nov</span>
          </p>
        </div>
      </article>

      <article *ngSwitchCase="'chat'" class="rounded-xl bg-white p-3 shadow-card-elevated ring-1 ring-black/5">
        <p class="mb-2 flex items-center gap-2 text-xs font-medium text-gray-500">
          <mat-icon class="!size-4 !text-base !leading-4">forum</mat-icon> Ikeja LGA corpers
        </p>
        <div class="flex items-end gap-2">
          <span class="flex size-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-light text-xs font-bold text-white">A</span>
          <p class="rounded-2xl rounded-bl-sm bg-background-light px-3 py-2 text-sm text-gray-900">Just posted to Ikeja. Where do corpers stay?</p>
        </div>
        <div class="mt-2 flex items-end justify-end gap-2">
          <p class="rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-sm text-white">Allen and Opebi. Check Houses, a few of us are leaving soon</p>
          <span class="flex size-7 flex-shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-accent">T</span>
        </div>
      </article>

      <article *ngSwitchCase="'ppa'" class="rounded-xl bg-white p-3 shadow-card-elevated ring-1 ring-black/5">
        <p class="text-sm font-semibold text-gray-900">Community Secondary School</p>
        <p class="text-xs text-gray-500">School · Ikeja, Lagos</p>
        <p class="mt-1 flex items-center gap-1 text-sm text-primary">
          <span class="flex text-[#E5A100]">
            <mat-icon *ngFor="let star of [1, 2, 3, 4]" class="!size-4 !text-base !leading-4">star</mat-icon>
            <mat-icon class="!size-4 !text-base !leading-4">star_half</mat-icon>
          </span>
          <strong class="font-semibold">4.5</strong><span class="text-xs text-gray-500">· 28 reviews</span>
        </p>
        <p class="mt-2 rounded-lg bg-background-light px-3 py-2 text-sm italic text-gray-700">"Staff are welcoming, and they respect your CDS day."</p>
      </article>
    </div>
  </div>

  <div class="px-5 py-6 md:px-8 space-y-8">
    <!-- Who are you? Each card is a journey; the steps below follow the one picked -->
    <section *ngIf="content.journeys?.length">
      <ng-container *ngIf="content.journeys!.length > 1">
        <h3 class="text-lg font-semibold text-primary mb-3">Which one are you?</h3>
        <div class="grid grid-cols-2 gap-3 mb-6" role="group" aria-label="Which one are you?">
          <button *ngFor="let journey of content.journeys" type="button" (click)="showJourney(journey)"
            [attr.aria-pressed]="journey === shownJourney[0]"
            class="flex flex-col items-start gap-2 rounded-xl p-3 text-left ring-1 transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            [ngClass]="journey === shownJourney[0] ? 'bg-primary text-white ring-primary' : 'bg-white text-primary ring-black/10 hover:ring-primary/40'">
            <mat-icon class="!size-6 !text-2xl" [ngClass]="journey === shownJourney[0] ? 'text-accent' : 'text-primary/70'">{{ journey.icon }}</mat-icon>
            <span>
              <span class="block font-semibold leading-snug">{{ journey.label }}</span>
              <span class="block mt-0.5 text-sm leading-snug" [ngClass]="journey === shownJourney[0] ? 'text-white/80' : 'text-text-secondary'">{{ journey.summary }}</span>
            </span>
          </button>
        </div>
      </ng-container>

      <ng-container *ngFor="let journey of shownJourney">
        <h3 class="text-lg font-semibold text-primary mb-4">How it works</h3>
        <ol>
          <li *ngFor="let step of journey.steps; let i = index; let last = last"
            class="step-in relative flex gap-4" [ngClass]="{ 'pb-5': !last }" [ngStyle]="{ 'animation-delay': i * 60 + 'ms' }">
            <span *ngIf="!last" aria-hidden="true" class="absolute left-4 top-9 bottom-1 w-0.5 -translate-x-1/2 rounded-full bg-primary/10"></span>
            <span class="relative flex flex-shrink-0 items-center justify-center size-8 rounded-full bg-accent text-primary text-sm font-bold">{{ i + 1 }}</span>
            <p class="pt-1 leading-snug">
              <span class="block font-semibold text-primary">{{ step.title }}</span>
              <span class="block mt-0.5 text-sm text-text-secondary [&_strong]:font-semibold [&_strong]:text-primary" [innerHTML]="step.detail"></span>
            </p>
          </li>
        </ol>
        <p *ngIf="journey.note" class="step-in mt-5 flex gap-2 text-sm text-text-secondary [&_strong]:font-semibold [&_strong]:text-primary"
          [ngStyle]="{ 'animation-delay': journey.steps.length * 60 + 'ms' }">
          <mat-icon class="!size-5 !text-xl flex-shrink-0 text-primary/60">info</mat-icon>
          <span [innerHTML]="journey.note"></span>
        </p>
      </ng-container>
    </section>

    <!-- What you can do (chat) -->
    <section *ngIf="content.features?.length">
      <h3 class="text-lg font-semibold text-primary mb-3">What you can do</h3>
      <ul class="space-y-3">
        <li *ngFor="let feature of content.features" class="flex items-center gap-3 text-primary">
          <mat-icon class="!size-5 !text-xl flex-shrink-0 text-primary/60">{{ feature.icon }}</mat-icon>
          <span>{{ feature.text }}</span>
        </li>
      </ul>
    </section>

    <!-- A pull quote: light, so it doesn't compete with the button -->
    <figure *ngIf="content.quote" class="border-l-4 border-accent-dark pl-4">
      <blockquote class="text-lg font-semibold leading-snug text-primary">{{ content.quote.text }}</blockquote>
      <figcaption *ngIf="content.quote.detail" class="mt-1 text-sm text-text-secondary">{{ content.quote.detail }}</figcaption>
    </figure>

    <!-- Exploring? Go on to the next service without closing -->
    <button type="button" (click)="showNext()"
      class="flex w-full items-center gap-3 rounded-xl p-3 text-left ring-1 ring-black/10 hover:ring-primary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary transition-colors duration-200">
      <span class="flex size-10 flex-shrink-0 items-center justify-center rounded-xl bg-background-light text-primary">
        <mat-icon class="!size-5 !text-xl">{{ next.icon }}</mat-icon>
      </span>
      <span class="flex-1 min-w-0">
        <span class="block text-sm text-text-secondary">Next</span>
        <span class="block truncate font-semibold text-primary">{{ next.title }}</span>
      </span>
      <mat-icon class="text-primary/60">arrow_forward</mat-icon>
    </button>
  </div>
</div>

<!-- The one action: always in reach, and it says where it goes -->
<footer class="flex-shrink-0 border-t border-gray-100 bg-white px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-8 md:flex md:justify-end">
  <a routerLink="/signup" [queryParams]="signupQueryParams" (click)="close()"
    class="btn-primary flex w-full md:w-auto items-center justify-center gap-2 min-h-12 !py-0 whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
    {{ content.cta.label }} <mat-icon class="!size-5 !text-xl !leading-5">arrow_forward</mat-icon>
  </a>
</footer>
